import React, { useState, useEffect, useRef } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup, { FORM_WIDE_UPLOAD_BUTTON_SX } from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyFaqDraft,
    clearFaqDraft,
    createEditFaqDraftKey,
    createFaqDraftPayload,
    FAQ_IMAGE_FILE_ACCEPT,
    FAQ_IMAGE_FILE_ERROR_MESSAGE,
    isFaqImageFile,
    readFaqDraft,
    saveFaqDraft,
} from './faqDraftUtils'
import {
    FAQ_IMAGE_LEAVE_MESSAGE,
    hasPendingFaqImage,
    usePendingFaqImageUnloadGuard,
} from './faqLeaveGuard'

const PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    width: '100%',
    height: { xs: 220, md: 280 },
    mb: 1.5,
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const FAQ_STATUS_OPTIONS = [
    {
        value: 0,
        title: '비활성화',
        description: 'FAQ를 숨기고 운영자만 수정할 수 있게 둡니다.',
    },
    {
        value: 1,
        title: '활성화',
        description: '저장 후 FAQ 목록과 상세 화면에 바로 노출합니다.',
    },
]

const getImageFileName = (imageFile) => {
    if (!imageFile || typeof imageFile !== 'object') {
        return ''
    }

    return imageFile.name || ''
}

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {children}
            </Box>
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function SettingChoiceGroup({ title, name, value, options, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Box
                role="radiogroup"
                aria-label={title}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {options.map((option) => {
                    const checked = String(value) === String(option.value)

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name={name}
                                    checked={checked}
                                    onChange={() => onChange(createFieldChangeEvent(name, option.value))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(checked, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function FaqImageUploadPanel({
    imageSrc,
    imageFile,
    description = 'FAQ 목록과 상세에서 함께 보이는 대표 이미지입니다.',
    disabled,
    onUploadClick,
}) {
    const imageFileName = getImageFileName(imageFile)

    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    FAQ 이미지
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="FAQ 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        이미지를 선택하면 미리보기가 표시됩니다.
                    </Typography>
                )}
            </Box>
            <FormControl fullWidth>
                <TextField
                    style={{ display: 'none' }}
                    inputProps={{ accept: FAQ_IMAGE_FILE_ACCEPT }}
                    id="image"
                    name="image"
                    type="file"
                    onChange={onUploadClick}
                    disabled={disabled}
                />
                <Box component="label" htmlFor="image" sx={{ display: 'block' }}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        component="span"
                        startIcon={<Add />}
                        sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                        disabled={disabled}
                    >
                        FAQ 이미지 선택
                    </Button>
                </Box>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {imageFileName ? `선택한 파일: ${imageFileName}` : '저장 전 미리보기로 이미지가 올바른지 확인하세요.'}
            </Typography>
        </Paper>
    )
}

export default function Faq(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.faqId
    const listCurPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchCategory: '' }
    const listSearch = location.search || ''
    const draftKey = createEditFaqDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [hasDetail, setHasDetail] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [data, setData] = useState({})
    const [mainImage, setMainImage] = useState('/images/noimage.jpg')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const initialDraftSnapshotRef = useRef('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const leaveGuardEnabled = hasPendingFaqImage(data) && !saving && !navigationUnlocked

    usePendingFaqImageUnloadGuard(leaveGuardEnabled)

    useEffect(() => {
        const getData = async () => {
            setLoading(true)
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage('')

            if (!id) {
                const errorMessage = '필수 파라미터가 없어 FAQ 상세를 불러올 수 없습니다.'
                setData({})
                setMainImage('/images/noimage.jpg')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            try {
                const { data } = await API.post('/api/v1/admin/faq/getFaq', { faqId: id })
                if (data.code === 1) {
                    const baseData = data.data || {}
                    const storedDraft = readFaqDraft(draftKey)
                    const restoredDraft = applyFaqDraft({
                        baseData,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createFaqDraftPayload({
                        data: baseData,
                    }))

                    setData(restoredDraft.data)
                    setHasDetail(Boolean(baseData?.faqId))
                    setDetailErrorMessage(baseData?.faqId ? '' : 'FAQ 상세 정보를 찾을 수 없습니다.')
                    setMainImage(!baseData?.image || baseData.image === '' ? '/images/noimage.jpg' : `${process.env.REACT_APP_SERVER_URL}/faq/${baseData.image}`)
                    setDraftReady(true)
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'FAQ 상세를 불러오지 못했습니다.'
                    setData({})
                    setMainImage('/images/noimage.jpg')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'FAQ 상세를 불러오지 못했습니다.'
                setData({})
                setMainImage('/images/noimage.jpg')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setLoading(false)
            }
        }

        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // FAQ 상세는 목록 query를 건드리지 않고 편집 draft만 sessionStorage에 보존한다.
        const nextDraft = createFaqDraftPayload({
            data,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearFaqDraft(draftKey)
            return
        }

        saveFaqDraft(draftKey, nextDraft)
    }, [data, draftKey, draftReady, hasDetail])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="FAQ 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/faqs" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={listCurPage} onBackClick={() => clearFaqDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)
        // 저장 후에도 운영자가 보던 FAQ 목록 조건으로 그대로 복귀시킨다.
        clearFaqDraft(draftKey)

        // 저장 후 이동은 이탈 경고를 잠시 풀고, 목록 조건은 그대로 유지한다.
        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: '/faqs',
                search: listSearch,
                curPage: listCurPage,
                searchText: searchText,
                searchSelect: searchSelect,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            clearFaqDraft(draftKey)
            moveToList()
            return
        }

        event.preventDefault()

        if (!window.confirm(FAQ_IMAGE_LEAVE_MESSAGE)) {
            return
        }

        moveToList()
    }

    const handleUpdateField = (e) => {
        setData({
            ...data,
            [e.target.name]: e.target.value
        })
    }

    const handleUploadClick = (e) => {
        e.preventDefault()
        const file = e.target.files?.[0]

        if (!file) {
            return
        }

        if (!isFaqImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(FAQ_IMAGE_FILE_ERROR_MESSAGE)
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setMainImage(reader.result)
            setData({
                ...data,
                [e.target.name]: file
            })
        }

        reader.readAsDataURL(file)
    }

    const updateData = async (params) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: '/api/v1/admin/faq/update',
                data: params,
                headers: { "Content-Type": "multipart/form-data" }
            })

            if (data.code === 1) {
                moveToList({ severity: 'success', message: 'FAQ를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('FAQ를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextTitle = (data.title || '').trim()
        const nextContent = (data.content || '').trim()

        setData((prev) => ({
            ...prev,
            title: nextTitle,
            content: nextContent
        }))

        if (!nextTitle) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('제목을 입력하세요.')
            return
        }

        if (!nextContent) {
            setOpenSnackbar(true)
            setSeveritySnackbar('warning')
            setMessageSnackbar('내용을 입력하세요.')
            return
        }

        const formData = new FormData()
        formData.append('faqId', data.faqId)
        formData.append('image', data.image)
        formData.append('category', data.category)
        formData.append('title', nextTitle)
        formData.append('content', nextContent)
        formData.append('order', data.order)
        formData.append('status', data.status)

        updateData(formData)
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!hasDetail) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>FAQ 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>FAQ 수정</Title>
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Prompt when={leaveGuardEnabled} message={FAQ_IMAGE_LEAVE_MESSAGE} />
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={7}>
                            <FormSection
                                title="FAQ 내용"
                                description="사용자 FAQ 목록과 상세에서 보여줄 제목과 답변 내용을 수정합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="제목"
                                        id="title"
                                        name="title"
                                        value={data.title || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                                <FormControl fullWidth>
                                    <TextField
                                        label="내용"
                                        id="content"
                                        name="content"
                                        multiline
                                        rows={10}
                                        value={data.content || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <FormSection
                                title="노출 설정"
                                description="카테고리와 노출 상태, 정렬 순서를 함께 확인합니다."
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        label="카테고리"
                                        select
                                        id="category"
                                        name="category"
                                        value={data.category || ''}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    >
                                        <MenuItem value=''>전체</MenuItem>
                                        <MenuItem value='1'>상품관련</MenuItem>
                                        <MenuItem value='2'>주문/결제</MenuItem>
                                        <MenuItem value='3'>배송</MenuItem>
                                        <MenuItem value='4'>변경/취소/반품</MenuItem>
                                        <MenuItem value='5'>회원정보</MenuItem>
                                        <MenuItem value='6'>서비스이용</MenuItem>
                                    </TextField>
                                </FormControl>
                                <SettingChoiceGroup
                                    title="상태"
                                    name="status"
                                    value={data.status ?? 1}
                                    options={FAQ_STATUS_OPTIONS}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                                <FormControl fullWidth>
                                    <TextField
                                        type="number"
                                        InputProps={{ inputProps: { min: 0, max: 99 } }}
                                        label="노출순서"
                                        id="order"
                                        name="order"
                                        value={data.order || 0}
                                        onChange={handleUpdateField}
                                        size="small"
                                        disabled={saving}
                                    />
                                </FormControl>
                            </FormSection>
                        </Grid>
                        <Grid item xs={12}>
                            <FaqImageUploadPanel
                                imageSrc={mainImage}
                                imageFile={data.image}
                                description="FAQ 목록과 상세에서 함께 보이는 대표 이미지입니다. 수정 시 이미지를 바꾸지 않으면 기존 이미지를 유지합니다."
                                disabled={saving}
                                onUploadClick={handleUploadClick}
                            />
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
